import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Eye, EyeOff, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { projectId } from "../utils/supabase/info";

interface UserDebugInfoProps {
  currentUser: any;
  accessToken?: string;
}

export function UserDebugInfo({ currentUser, accessToken }: UserDebugInfoProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [settingDemo, setSettingDemo] = useState(false);

  const handleToggleDemoMode = async () => {
    if (!accessToken || !currentUser?.id) {
      toast.error('Missing authentication');
      return;
    }

    setSettingDemo(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/users/${currentUser.id}/toggle-demo`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to toggle demo mode');
      }

      const data = await response.json();
      toast.success(`Demo mode ${data.isDemo ? 'enabled' : 'disabled'}. Refreshing page...`);
      
      // Refresh the page to load updated user data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error toggling demo mode:', error);
      toast.error('Failed to toggle demo mode');
    } finally {
      setSettingDemo(false);
    }
  };

  if (!isVisible) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 left-4 z-50 opacity-50 hover:opacity-100"
      >
        <Eye className="h-4 w-4 mr-2" />
        Debug Info
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-4 left-4 z-50 w-96 shadow-lg max-h-[80vh] flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">User Debug Info</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsVisible(false)}
          >
            <EyeOff className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>Current user session details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 overflow-y-auto flex-1">
        {/* User ID */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">User ID</p>
          <p className="text-sm font-mono break-all">{currentUser?.id || 'N/A'}</p>
        </div>

        {/* Email */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Email</p>
          <p className="text-sm">{currentUser?.email || 'N/A'}</p>
        </div>

        {/* Name */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Name</p>
          <p className="text-sm">{currentUser?.name || 'N/A'}</p>
        </div>

        {/* Role */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Role</p>
          <Badge variant="default">{currentUser?.role || 'N/A'}</Badge>
        </div>

        {/* Company ID */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Company ID</p>
          <p className="text-sm font-mono break-all">{currentUser?.companyId || 'N/A'}</p>
        </div>

        {/* Firm ID */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Firm ID</p>
          <p className="text-sm font-mono break-all">{currentUser?.firmId || 'N/A'}</p>
        </div>

        {/* Flags */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Flags</p>
          <div className="flex flex-wrap gap-1">
            {currentUser?.isDemo && <Badge variant="secondary" className="bg-orange-100 text-orange-700 border-orange-300"><FlaskConical className="size-3 mr-1" />isDemo</Badge>}
            {currentUser?.isAdmin && <Badge variant="secondary">isAdmin</Badge>}
            {currentUser?.isCompanyAdmin && <Badge variant="secondary">isCompanyAdmin</Badge>}
            {!currentUser?.isDemo && !currentUser?.isAdmin && !currentUser?.isCompanyAdmin && (
              <Badge variant="outline">Regular User</Badge>
            )}
          </div>
        </div>

        {/* Access Token Preview */}
        {accessToken && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">Access Token (first 20 chars)</p>
            <p className="text-xs font-mono break-all">{accessToken.substring(0, 20)}...</p>
          </div>
        )}

        {/* Role Permissions */}
        <div className="border-t pt-3">
          <p className="text-xs text-muted-foreground mb-2">Calculated Permissions</p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span>System Admin</span>
              <Badge variant={currentUser?.role === 'systemAdmin' || currentUser?.isAdmin ? 'default' : 'outline'}>
                {currentUser?.role === 'systemAdmin' || currentUser?.isAdmin ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>Consultant</span>
              <Badge variant={currentUser?.role === 'consultant' ? 'default' : 'outline'}>
                {currentUser?.role === 'consultant' ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>CF Admin</span>
              <Badge variant={
                currentUser?.role === 'cfAdmin' || 
                currentUser?.role === 'CFAdmin' || 
                currentUser?.role === 'consulting_firm_admin' 
                  ? 'default' 
                  : 'outline'
              }>
                {currentUser?.role === 'cfAdmin' || 
                 currentUser?.role === 'CFAdmin' || 
                 currentUser?.role === 'consulting_firm_admin' 
                  ? 'Yes' 
                  : 'No'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>Company Admin</span>
              <Badge variant={
                currentUser?.isCompanyAdmin || currentUser?.role === 'companyAdmin' 
                  ? 'default' 
                  : 'outline'
              }>
                {currentUser?.isCompanyAdmin || currentUser?.role === 'companyAdmin' ? 'Yes' : 'No'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Required for /admin/users endpoint */}
        <div className="border-t pt-3">
          <p className="text-xs text-muted-foreground mb-2">Can Access /admin/users?</p>
          <Badge variant={
            currentUser?.role === 'systemAdmin' || 
            currentUser?.isAdmin || 
            currentUser?.role === 'consultant' ||
            currentUser?.role === 'cfAdmin' ||
            currentUser?.role === 'CFAdmin' ||
            currentUser?.role === 'consulting_firm_admin'
              ? 'default'
              : 'destructive'
          }>
            {currentUser?.role === 'systemAdmin' || 
             currentUser?.isAdmin || 
             currentUser?.role === 'consultant' ||
             currentUser?.role === 'cfAdmin' ||
             currentUser?.role === 'CFAdmin' ||
             currentUser?.role === 'consulting_firm_admin'
              ? 'Yes - Authorized'
              : 'No - 403 Forbidden'}
          </Badge>
        </div>

        {/* Toggle Demo Mode */}
        <div className="border-t pt-3">
          <p className="text-xs text-muted-foreground mb-2">Demo Mode</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleDemoMode}
            disabled={settingDemo}
          >
            {settingDemo ? 'Toggling...' : 'Toggle Demo Mode'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}